/**
 * Usuarios (solo el administrador): invitar a familia y amigos, darlos de baja y reactivarlos.
 * Quien decide de verdad es el servidor: esta pantalla solo se enseña al administrador.
 */
import { api, type UserSummary } from './adapters/api-client.js';
import { wipeDevice } from './adapters/device.js';
import { currentUser, isAdmin } from './adapters/session.js';
import { esc, dayLabel } from './ui/format.js';
import { toast } from './ui/feedback.js';
import { frame, setView, type ViewState } from './views.js';

const when = (iso: string) => (iso ? dayLabel(iso.slice(0, 10)) : '');

function statusOf(u: UserSummary): string {
  if (u.role === 'admin') return 'Administrador (tú)';
  if (!u.active) return `Dado de baja${u.deactivatedAt ? ` el ${esc(when(u.deactivatedAt))}` : ''}`;
  if (!u.lastSignInAt) return 'Invitado · aún no ha entrado';
  return `Activo · entró el ${esc(when(u.lastSignInAt))}${u.hasWorkbook ? '' : ' · sin libro todavía'}`;
}

export function viewUsers(s: ViewState) {
  frame(s, { title: 'Usuarios', tab: 'mas', back: true });
  if (s.demo || !isAdmin()) return setView('<section class="card"><h2>Usuarios</h2><div class="sub">Solo el administrador gestiona los usuarios.</div></section>');
  let invited = '';
  /** Botones de la lista. Va después de pintar: setView quita los manejadores de la vista anterior. */
  const onClick = async (e: MouseEvent) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('button');
    if (!b) return;
    if (b.dataset.act === 'copy') {
      try { await navigator.clipboard.writeText(location.origin); toast('Enlace copiado', 'ok'); }
      catch { toast(`Cópialo a mano: ${location.origin}`, 'error'); }
      return;
    }
    try {
      if (b.dataset.off) {
        if (!confirm(`¿Dar de baja a ${b.dataset.who}? Dejará de poder usar la web al momento y sus Atajos dejarán de funcionar. Su libro sigue en su Google Drive.`)) return;
        await api.admin.setActive(b.dataset.off, false); toast('Dado de baja', 'ok');
      } else if (b.dataset.on) {
        await api.admin.setActive(b.dataset.on, true); toast('Reactivado: ya puede volver a entrar con Google', 'ok');
      } else return;
      invited = '';
      await load();
    } catch (err) { toast((err as Error).message, 'error'); }
  };
  const draw = (users: UserSummary[]) => {
    setView(`
      <section class="card"><h2>Invitar</h2>
        <div class="sub hint">Escribe el email de su cuenta de Google. Entrará con Google y la app le guiará para crear su libro, que estará en su propio Google Drive.</div>
        <form id="inviteForm">
          <div class="field"><label for="invEmail">Email de Google</label><input id="invEmail" type="email" inputmode="email" autocomplete="off" placeholder="nombre@gmail.com" required></div>
          <div class="split" style="align-items:flex-end">
            <div class="field"><label for="invName">Nombre (para ti)</label><input id="invName" maxlength="60" placeholder="Pili"></div>
            <div class="field" style="flex:0 0 auto"><button class="btn" type="submit">Invitar</button></div>
          </div>
        </form>
        ${invited ? `<div class="recon ok" style="margin-top:10px">✓ <b>${esc(invited)}</b> ya puede entrar. Pásale este enlace: <b>${esc(location.origin)}</b>
          <button class="linklike" data-act="copy">Copiar enlace</button></div>` : ''}
      </section>
      <section class="card"><h2>Quién tiene acceso (${users.filter(u => u.active).length})</h2><div class="list">
        ${users.map(u => `<div class="row"><div class="main"><div class="title">${esc(u.displayName || u.email)}</div>
          ${u.displayName ? `<div class="meta">${esc(u.email)}</div>` : ''}<div class="meta user-status">${statusOf(u)}</div></div>
          ${u.role === 'admin' ? '' : u.active
            ? `<button class="btn small secondary" data-off="${esc(u.id)}" data-who="${esc(u.displayName || u.email)}">Dar de baja</button>`
            : `<button class="btn small secondary" data-on="${esc(u.id)}">Reactivar</button>`}</div>`).join('')}
      </div>
      <div class="sub hint">Dar de baja corta el acceso al momento: la web y sus Atajos dejan de funcionar. Su libro sigue en su Google Drive y es suyo. Solo tú ves esta pantalla.</div></section>`);
    document.getElementById('view')!.onclick = e => { void onClick(e); };
    const form = document.getElementById('inviteForm') as HTMLFormElement;
    form.onsubmit = async e => {
      e.preventDefault();
      const email = (document.getElementById('invEmail') as HTMLInputElement).value.trim();
      const name = (document.getElementById('invName') as HTMLInputElement).value.trim();
      try { const u = await api.admin.invite(email, name); invited = u.displayName || u.email; await load(); }
      catch (err) { toast((err as Error).message, 'error'); }
    };
  };
  const load = async () => { try { draw(await api.admin.users()); } catch (err) { setView(`<div class="error">${esc((err as Error).message)}</div>`); } };
  setView('<div class="loading">Cargando usuarios…</div>');
  void load();
}

/** «Tu cuenta» en Más: quién eres y, si no eres el administrador, borrar tu cuenta. */
export function accountCard(demo: boolean): string {
  const me = currentUser();
  if (demo || !me?.email) return '';
  return `<section class="card"><h2>Tu cuenta</h2>
    <div class="sub">Has entrado como <b>${esc(me.email)}</b>.</div>
    ${me.role === 'admin' ? '' : `<div class="sub hint">Si borras tu cuenta, se borra tu acceso y lo que la app guarda de ti en su servidor (permisos, claves de Atajos, preferencias). Tu libro de Google Sheets no se borra: sigue en tu Drive.</div>
      <div class="actions"><button class="btn secondary small" id="deleteAccount">Borrar mi cuenta</button></div>`}
  </section>`;
}

export async function deleteAccountFlow() {
  if (!confirm('¿Borrar tu cuenta? Dejarás de poder entrar y se borrará lo que la app guarda de ti. Tu libro sigue en tu Google Drive.')) return;
  if (!confirm('Última confirmación: ¿borrar tu cuenta ahora?')) return;
  try { await api.deleteAccount(); } catch (err) { return toast((err as Error).message, 'error'); }
  await wipeDevice();
  location.href = '/';
}
